import { Component, EventEmitter, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { QuoteRejectReasonDialogComponent } from '@app/matrix/components/quote-reject-reason-dialog/quote-reject-reason-dialog.component';
import { Quote, QuoteState } from '@app/quoting/models/quote';
import { ApprovalAccept, ApprovalReject, OfferReject, QuotingModuleActionTypes } from '@quoting/state/actions/quoting-module.actions';
import { ResolveOffersDialogComponent } from '@app/shared/components/resolve-offers-dialog/resolve-offers-dialog.component';
import { TextAreaDialogComponent } from '@app/shared/components/text-area-dialog/text-area-dialog.component';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { AppState } from '@app/state/app.state';
import { Store } from '@ngrx/store';
import LogRocket from 'logrocket';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import { Subscription } from 'rxjs';
import { ShareQuoteDialogComponent } from '../share-quote-dialog/share-quote-dialog.component';
import { quoteSelector, utilityInformationFeatureSelector } from '@app/state/app.selectors';
import { selectQuote } from '@quoting/state/selectors/quoting-module.selectors';
import { QuoteRequestState } from '@app/shared/models/quote-request-state';
import { QuoteStateHelperService } from '@app/shared/services/quote-state-helper.service';

@Component({
  selector: 'app-quote-review-actions',
  templateUrl: './quote-review-actions.component.html',
  styleUrls: ['./quote-review-actions.component.scss'],
})
export class QuoteReviewActionsComponent implements OnInit, OnDestroy, OnChanges {
  public quoteStateType = QuoteState;
  public quoteState: QuoteState;
  public quoteRequestState: QuoteRequestState;
  public quoteRequestType = QuoteRequestType;
  approvedTerms: number[];

  public showShareQuoteBtn$: Subscription;
  public showShareQuoteBtn: boolean;

  @Output() offersActioned = new EventEmitter<QuotingModuleActionTypes>();

  public quote: Quote;
  selectQuote$: Subscription;
  resolveDialog$: Subscription;
  rejectDialog$: Subscription;
  rejectQuoteRequest$: Subscription;
  resolveDialogClosed$: Subscription;
  shareQuoteDialog$: Subscription;

  constructor(private dialog: MatDialog, private state: Store<AppState>, public quoteService: QuoteRequestService, public quoteStateHelperService: QuoteStateHelperService) {
    this.selectQuote$ = state.select(quoteSelector).subscribe((s) => {
      this.quote = s.quote;
      this.quoteState = s.quote?.quoteState;
      this.quoteRequestState = s.quote?.quoteRequestState;
    });
    this.showShareQuoteBtn$ = state.select(utilityInformationFeatureSelector).subscribe((s) => {
      this.showShareQuoteBtn = s.showShareQuoteButton;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {}

  ngOnDestroy(): void {
    this.selectQuote$?.unsubscribe();
    this.resolveDialog$?.unsubscribe();
    this.rejectDialog$?.unsubscribe();
    this.rejectQuoteRequest$?.unsubscribe();
    this.resolveDialogClosed$?.unsubscribe();
    this.shareQuoteDialog$?.unsubscribe();
    this.showShareQuoteBtn$.unsubscribe();
  }

  ngOnInit(): void {}

  

  public rejectAllOffersRequiringApproval(): void {
    const resolveDialog = this.dialog.open(ResolveOffersDialogComponent, {
      width: '40%',
      data: {
        title: 'Reject Request',
        quoteRequestId: this.quote.quoteRequestState.quoteRequestId,
        approvedTerms: this.quote.prices.map((x) => x.term),
        rejectAll: true,
      },
    });

    this.resolveDialog$ = resolveDialog.afterClosed().subscribe((result) => {
      if (result.success) {
        this.state.dispatch(new ApprovalReject(this.quote.quoteRequestState.quoteRequestId));
        LogRocket.track('supplier_rejected', { quoteRequestId: this.quote.quoteRequestState.quoteRequestId });
      }
    });
  }

  public shareQuote() {
    const shareQuoteDialog = this.dialog.open(ShareQuoteDialogComponent, {
      width: '40%',
      data: {
        title: 'Share Quote',
        quoteRequestId: this.quote.quoteRequestState.quoteRequestId,
        quoteType: this.quote.quoteRequestState.quoteRequestType
      },
    });

    this.shareQuoteDialog$ = shareQuoteDialog.afterClosed().subscribe((result) => {
      if (result.confirmed) {
        console.log('rejected')
      }
    });
  }

  public rejectAllOffers() {
    const rejectDialog = this.dialog.open(QuoteRejectReasonDialogComponent, {
      width: '40%',
      data: {
        title: 'Offer Rejection Reason',
        maxCharacters: 255,
        quoteRequestId: this.quote.quoteRequestState.quoteRequestId,
      },
    });

    this.rejectDialog$ = rejectDialog.afterClosed().subscribe((result) => {
      if (result.confirmed) {
        this.state.dispatch(new OfferReject(this.quote.quoteRequestState.quoteRequestId));
        LogRocket.track('offers_rejected', { quoteRequestId: this.quote.quoteRequestState.quoteRequestId });
        this.offersActioned.emit(QuotingModuleActionTypes.OfferReject);
        window.location.reload();
      }
    });
  }

  public rejectRequest() {
    const resolveDialog = this.dialog.open(TextAreaDialogComponent, {
      width: '40%',
      data: {
        title: 'Reject Request', 
        textAreaTitle: 'Request Rejection Reason',
      },
    });

    this.resolveDialog$ = resolveDialog.afterClosed().subscribe((result) => {
      if (result.success) {
        const requestRejection = {
          notes: result.textAreaInput,
        };
        this.rejectQuoteRequest$ = this.quoteService
          .rejectQuoteRequest(this.quote.quoteRequestState.quoteRequestId, requestRejection)
          .subscribe((x) => {
            this.state.dispatch(new ApprovalReject(this.quote.quoteRequestState.quoteRequestId));
            this.offersActioned.emit(QuotingModuleActionTypes.ApprovalReject);
            window.location.reload();
          });
      }
    });
  }

  public sendAllOffers(): void {
    const resolveDialog = this.dialog.open(ResolveOffersDialogComponent, {
      width: '40%',
      data: {
        title: 'Send Offers',
        quoteRequestId: this.quote.quoteRequestState.quoteRequestId,
        approvedTerms: this.quote.prices.map((x) => x.term),
      },
    });

    this.resolveDialogClosed$ = resolveDialog.afterClosed().subscribe((result) => {
      if (result.success) {
        this.state.dispatch(new ApprovalAccept(this.quote.prices));
        this.offersActioned.emit(QuotingModuleActionTypes.ApprovalAccept);
      }
    });
  }
}
